import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Attachable } from '../../elements/models/attachable.model';
import { emptyAttachable } from '../../elements/models/attachable.model';
import { ApiError } from '../../models/api-error.enum';
import type { AccountSettingsResponse } from '../../models/common.model';
import type { Loadable } from '../../models/loadable.model';
import { emptyLoadable } from '../../models/loadable.model';
import { windowNavigate } from '../../utils/window.utils';
import type {
  AccountType,
  AuthenticationResult,
  CheckAuthenticationResponse,
  CustomerResponse,
} from '../models/auth.model';
import { authenticationFlowService } from '../services/authentication-flow.service';
import { customerAuthService } from '../services/customer-auth.service';
import {
  attachLoginButton,
  attachLoginSection,
  attachLogoutButton,
  attachLogoutSection,
  attachWaitingSection,
  checkAccountType,
  checkAuthentication,
  initializeCustomer,
  loadAccountSettings,
} from './auth.thunk';

export interface IAuthState {
  loggedIn: boolean;
  customer: Loadable<CustomerResponse>;
  authentication: Loadable<CheckAuthenticationResponse>;
  authenticationResult: AuthenticationResult | null;
  accountType: Loadable<AccountType>;
  loginButton: Attachable<HTMLElement>;
  loginSection: Attachable<HTMLElement[]>;
  logoutButton: Attachable<HTMLElement>;
  logoutSection: Attachable<HTMLElement[]>;
  waitingSection: Attachable<HTMLElement>;
  authInitiator: Attachable<HTMLElement>;
  accountSettingsResponse: Loadable<AccountSettingsResponse>;
}

const initialState: IAuthState = {
  loggedIn: false,
  customer: emptyLoadable(),
  authentication: emptyLoadable(),
  authenticationResult: null,
  accountType: emptyLoadable(),
  loginButton: emptyAttachable(),
  loginSection: emptyAttachable(),
  logoutButton: emptyAttachable(),
  logoutSection: emptyAttachable(),
  waitingSection: emptyAttachable(),
  authInitiator: emptyAttachable(),
  accountSettingsResponse: emptyLoadable(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Regular synchronous actions here (if needed)
    login: (state) => {
      state.loggedIn = false;
    },
    loginResult: (state, action: PayloadAction<{ success: boolean }>) => {
      state.loggedIn = action.payload.success;
    },
    logout: () => {
      customerAuthService.clearCustomer();
      return initialState;
    },
    authenticationResult: (state, action: PayloadAction<AuthenticationResult>) => {
      state.authenticationResult = action.payload;
      if (action.payload.success && action.payload.loadUrl) {
        authenticationFlowService.storeAuthenticationResult(action.payload);
        windowNavigate(action.payload.loadUrl);
      } else if (!action.payload.success && action.payload.rejectUrl) {
        windowNavigate(action.payload.rejectUrl);
      }
    },
    processStoredAuthenticationResult: () => {
      authenticationFlowService.processStoredAuthenticationResult();
    },
  },
  extraReducers: (builder) => {
    // checkAuthentication thunk handler
    builder
      .addCase(checkAuthentication.pending, (state: IAuthState) => {
        state.authentication.loading = true;
        state.authentication.error = undefined;
      })
      .addCase(checkAuthentication.fulfilled, (state: IAuthState, action: any) => {
        state.loggedIn = action.payload?.authenticated;
        state.authentication.loading = false;
        state.authentication.loaded = true;
        state.authentication.error = undefined;
        state.authentication.value = action.payload;
      })
      .addCase(checkAuthentication.rejected, (state: IAuthState, action: any) => {
        state.loggedIn = false;
        state.authentication.loading = false;
        state.authentication.loaded = false;
        state.authentication.error = action.payload ?? ApiError.Other;
      });
    // initializeCustomer thunk handler
    builder
      .addCase(initializeCustomer.pending, (state: IAuthState) => {
        state.customer.loading = true;
        state.customer.loaded = false;
        state.customer.error = undefined;
      })
      .addCase(initializeCustomer.fulfilled, (state: IAuthState, action: any) => {
        state.customer.loading = false;
        state.customer.loaded = true;
        state.customer.error = undefined;
        state.customer.value = action.payload;
      })
      .addCase(initializeCustomer.rejected, (state: IAuthState, action: any) => {
        state.customer.loading = false;
        state.customer.loaded = false;
        state.customer.error = action.payload ?? ApiError.Other;
      });
    // checkAccountType thunk handler
    builder
      .addCase(checkAccountType.pending, (state: IAuthState) => {
        state.accountType.loading = true;
        state.accountType.loaded = false;
        state.accountType.error = undefined;
      })
      .addCase(checkAccountType.fulfilled, (state: IAuthState, action: any) => {
        state.accountType.loading = false;
        state.accountType.loaded = true;
        state.accountType.error = undefined;
        state.accountType.value = action.payload;
      })
      .addCase(checkAccountType.rejected, (state: IAuthState, action: any) => {
        state.accountType.loading = false;
        state.accountType.loaded = false;
        state.accountType.error = action.payload ?? ApiError.Other;
      });
    // loadAccountSettings thunk handler
    builder
      .addCase(loadAccountSettings.pending, (state: IAuthState) => {
        state.accountSettingsResponse.loading = true;
        state.accountSettingsResponse.loaded = false;
        state.accountSettingsResponse.error = undefined;
      })
      .addCase(loadAccountSettings.fulfilled, (state: IAuthState, action: any) => {
        state.accountSettingsResponse.loading = false;
        state.accountSettingsResponse.loaded = true;
        state.accountSettingsResponse.error = undefined;
        state.accountSettingsResponse.value = action.payload;
      })
      .addCase(loadAccountSettings.rejected, (state: IAuthState, action: any) => {
        state.accountSettingsResponse.loading = false;
        state.accountSettingsResponse.loaded = false;
        state.accountSettingsResponse.error = action.payload ?? ApiError.Other;
      });
    // Attach Auth Elements
    builder
      .addCase(attachLoginButton.pending, (state: IAuthState) => {
        state.loginButton = emptyAttachable();
      })
      .addCase(attachLoginButton.fulfilled, (state: IAuthState, action) => {
        state.loginButton = {
          value: action.payload.button,
          attached: action.payload.success,
        };
      })
      .addCase(attachLogoutButton.pending, (state: IAuthState) => {
        state.logoutButton = emptyAttachable();
      })
      .addCase(attachLogoutButton.fulfilled, (state: IAuthState, action) => {
        state.logoutButton = {
          value: action.payload.button,
          attached: action.payload.success,
        };
      })
      .addCase(attachLoginSection.pending, (state: IAuthState) => {
        state.loginSection = emptyAttachable();
      })
      .addCase(attachLoginSection.fulfilled, (state: IAuthState, action) => {
        state.loginSection = {
          value: action.payload.sections,
          attached: action.payload.success,
        };
      })
      .addCase(attachLogoutSection.pending, (state: IAuthState) => {
        state.logoutSection = emptyAttachable();
      })
      .addCase(attachLogoutSection.fulfilled, (state: IAuthState, action) => {
        state.logoutSection = {
          value: action.payload.sections,
          attached: action.payload.success,
        };
      })
      .addCase(attachWaitingSection.pending, (state: IAuthState) => {
        state.waitingSection = emptyAttachable();
      })
      .addCase(attachWaitingSection.fulfilled, (state: IAuthState, action) => {
        state.waitingSection = {
          value: action.payload.section,
          attached: action.payload.success,
        };
      });
  },
});

export const { login, loginResult, logout, authenticationResult } = authSlice.actions;
export const authReducer = authSlice.reducer;
